import * as React from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import classNames from 'classnames';
import Modal from '@mui/material/Modal';
import X from 'assets/x.inline.svg';
import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';
import * as styles from './Categories.module.scss';
import { BlogTaxonomy } from '../../lib/types';

export type CategoriesProps = {
    selectedTag: string;
    selectedCategory: string;
    searchTerm: string;
};

type TaxonomiesQuery = {
    allBlogEntry: {
        nodes: ReadonlyArray<{
            categories: ReadonlyArray<BlogTaxonomy>;
            tags: ReadonlyArray<BlogTaxonomy>;
        }>;
    };
};

export const taxonomiesQuery = graphql`
    query TaxonomiesQuery {
        allBlogEntry {
            nodes {
                categories {
                    slug
                    name
                }
                tags {
                    name
                    slug
                }
            }
        }
    }
`;

type NavItemProps = {
    to: string;
    label: string;
    selected: boolean;
};

function NavItem({ to, label, selected }: NavItemProps) {
    return (
        <li
            className={classNames(styles.item, {
                [styles.activeItem]: selected,
            })}
        >
            <Link to={to}>{label}</Link>
        </li>
    );
}

function Categories({
    selectedCategory,
    selectedTag,
    searchTerm,
}: CategoriesProps): React.ReactElement {
    const {
        allBlogEntry: { nodes: blogEntries },
    } = useStaticQuery<TaxonomiesQuery>(taxonomiesQuery);

    const categories = uniqWith(
        blogEntries.reduce((a: BlogTaxonomy[], b) => {
            return [...a, ...b.categories];
        }, []),
        isEqual,
    );

    const unfilteredTags = React.useMemo(
        () =>
            uniqWith(
                blogEntries.reduce(
                    (a: BlogTaxonomy[], b) => [...a, ...b.tags],
                    [],
                ),
                isEqual,
            ),
        [blogEntries],
    );

    const tags = React.useMemo(() => {
        let filteredTags;
        if (selectedCategory === 'dogs') {
            filteredTags = unfilteredTags.filter(
                (tag) => tag.slug !== 'kittens',
            );
        } else if (selectedCategory === 'cats') {
            filteredTags = unfilteredTags.filter(
                (tag) => tag.slug !== 'puppies',
            );
        } else {
            filteredTags = unfilteredTags;
        }

        return filteredTags;
    }, [selectedCategory, unfilteredTags]);

    const [modalOpen, setModalOpen] = React.useState(false);

    const toggleModal = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setModalOpen((wasOpen) => !wasOpen);
            });
        });
    };

    const content = (
        <nav className={styles.nav}>
            <ul>
                <NavItem
                    selected={selectedCategory === 'all'}
                    to={`/blog${searchTerm ? `?q=${searchTerm}` : ''}`}
                    label="All"
                />
                {categories.map(({ slug, name }) => (
                    <NavItem
                        key={slug}
                        selected={selectedCategory === slug}
                        to={`/blog/${slug}${
                            searchTerm ? `?q=${searchTerm}` : ''
                        }`}
                        label={name}
                    />
                ))}
            </ul>
            <ul>
                <NavItem
                    selected={selectedTag === 'all'}
                    to={`/blog${
                        selectedCategory === 'all' ? '' : `/${selectedCategory}`
                    }${searchTerm ? `?q=${searchTerm}` : ''}`}
                    label="All"
                />
                {tags.map(({ slug, name }) => (
                    <NavItem
                        key={slug}
                        selected={selectedTag === slug}
                        to={`/blog/${selectedCategory}/${slug}${
                            searchTerm ? `?q=${searchTerm}` : ''
                        }`}
                        label={name}
                    />
                ))}
            </ul>
        </nav>
    );

    return (
        <>
            <div className={styles.root}>
                <button
                    className={styles.toggle}
                    type="button"
                    onClick={toggleModal}
                >
                    Categories
                </button>
                {content}
            </div>
            <Modal
                open={modalOpen}
                onBackdropClick={toggleModal}
                aria-labelledby="blog-categories-modal-title"
                BackdropProps={{ className: styles.curtain }}
                className={styles.modalWrapper}
            >
                <div className={styles.modal}>
                    <h3>Categories</h3>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={toggleModal}
                    >
                        <X className={styles.xIcon} />
                        <span className="sr-only">Close</span>
                    </button>
                    {content}
                </div>
            </Modal>
        </>
    );
}

export default Categories;
